import moment from 'moment';
import { formatDate } from 'renderer/shared/formatters/formatters';
import { License } from '../schemas/licenses.schemas';

/** Subscription plan product roots from the license server. */
const SUBSCRIPTION_PRODUCT_ROOTS = [
  'com.digitalanarchy.AnarchyAllAccess',
  'com.digitalanarchy.Anarchy',
] as const;

export const isSubscriptionProductLicense = (license: License): boolean => {
  const { name } = license.product;
  if (!name) return false;
  return SUBSCRIPTION_PRODUCT_ROOTS.some(
    (root) => name === root || name.startsWith(`${root}.`),
  );
};

/**
 * Trial serials have an expiration but are not subscription licenses
 * (matches license server: expiration && !subscription).
 */
export const isTrialLicense = (license: License): boolean =>
  Boolean(license.expiration) &&
  !license.subscription &&
  !isSubscriptionProductLicense(license);

/** Subscription serial in grace period (e.g. after downgrade) before expiration. */
export const isSubscriptionExpiringLicense = (license: License): boolean =>
  (Boolean(license.subscription) || isSubscriptionProductLicense(license)) &&
  Boolean(license.expiration) &&
  moment(license.expiration).isAfter(moment());

export const isLicenseExpired = (license: License): boolean => {
  if (!license.expiration) return false;
  return moment(license.expiration).isBefore(moment());
};

export const isLicenseEffectivelyActive = (license: License): boolean => {
  if (isLicenseExpired(license)) return false;
  return license.active;
};

const formatExpirationDate = (expiration: string) =>
  moment(expiration).format('MM/DD/YY');

export type LicenseStatusChip = {
  label: string;
  color: 'success' | 'error' | 'warning';
  useCustomWarningStyle: boolean;
};

export const getLicenseStatusChip = (license: License): LicenseStatusChip => {
  if (isLicenseExpired(license) || !license.active) {
    return {
      label: 'Deactivated',
      color: 'error',
      useCustomWarningStyle: false,
    };
  }

  if (isTrialLicense(license)) {
    return {
      label: `Trial Expires\n${formatExpirationDate(license.expiration!)}`,
      color: 'warning',
      useCustomWarningStyle: true,
    };
  }

  if (isSubscriptionExpiringLicense(license)) {
    return {
      label: `Expires\n${formatExpirationDate(license.expiration!)}`,
      color: 'warning',
      useCustomWarningStyle: true,
    };
  }

  return {
    label: 'Active',
    color: 'success',
    useCustomWarningStyle: false,
  };
};

export const getLicensePurchasedDisplay = (license: License): string => {
  if (
    license.expiration &&
    (isTrialLicense(license) || isSubscriptionExpiringLicense(license))
  ) {
    return formatExpirationDate(license.expiration);
  }
  return formatDate(new Date(license.purchased));
};
